import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  CapabilityGrants,
  HostError,
  MINIMUM_COMPATIBLE_SDK_VERSION,
  WIDGET_PROTOCOL,
  WidgetHostHandlers,
  isSdkVersionCompatible,
  manifestSdkVersion,
} from '@finxone-platform/shared/app-sdk';
import { NavigationOptionsType } from '@finxone-platform/shared/sys-config-types';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AccountService } from '../../../../lib/services/account-service/account-service.service';
import { CardManagementService } from '../../../../lib/services/card-management-service/card-management-service';
import { CustomFunctionsService } from '../../../../lib/services/custom-functions/custom-functions.service';
import { DocumentService } from '../../../../lib/services/document-service/document.service';
import { NotificationService } from '../../../../lib/services/notification-service/notification-service.service';
import { PasswordService } from '../../../../lib/services/password-service/password-service.service';
import { DownloadDocumentService } from '../../../../lib/services/download-document/download-document.service';
import { ExchangeService } from '../../../../lib/services/exchange-service/exchange-service.service';
import { NavigationService } from '../../../../lib/services/navigation-service/navigation.service';
import { SavingsAccountService } from '../../../../lib/services/savings-account-service/savings-account.service';
import { TransactionService } from '../../../../lib/services/transaction-service/transaction.service';
import { WorkflowFormsService } from '../../../../lib/services/workflow-forms/workflow-forms.service';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { WidgetHandoffService } from '../../../../lib/services/widget-handoff/widget-handoff.service';
import { WidgetInstanceScope } from '../../../../lib/services/widget-handoff/widget-handoff.type';
import { IdentityVerificationService } from '../../../../lib/services/identity-verification/identity-verification.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { CompaniesHouseService } from '../../../../lib/services/companies-house-service/companies-house.service';
import { HeadlessAuthFlowService } from '../../../../lib/services/headless-auth-flow/headless-auth-flow.service';
import { KeycloakWrapperService } from '../../../../lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { SocketService } from '../../../../lib/services/socket-service/socket-service.service';
import { buildCustomWidgetApiOperations } from './custom-widget-api-operations';
import { buildCustomWidgetAuthBridge } from './custom-widget-auth';
import { ConfirmationRunner } from './custom-widget-confirmation';
import { buildCustomWidgetDataTopics } from './custom-widget-data-topics';
import { buildCustomWidgetHandoffBridge, withHandoffsTopic } from './custom-widget-handoff';
import { buildCustomWidgetLookupOperations } from './custom-widget-lookup-operations';
import { DataSourceService } from '../../../../lib/services/data-source-service/data-source.service';
import { buildCustomWidgetOnboardingBridge, withOnboardingStatusTopic } from './custom-widget-onboarding';
import { firstValueFrom } from 'rxjs';

/**
 * Instance configuration carried in
 * attributes.widgetProperties['customWidget'] of a CUSTOM_WIDGET.
 */
export interface CustomWidgetInstanceProperties {
  widgetId: string;
  version: string;
  /** Same-origin bundle path, e.g. /assets/custom-widgets/<id>/<version>/runner.html */
  /** Legacy persisted URL. New placements resolve from slug/version at render time. */
  bundleUrl?: string;
  settings?: Record<string, unknown>;
  grants?: Partial<CapabilityGrants>;
  defaultHeight?: number;
  /**
   * The activated manifest's `protocol` and `sdkVersion`, copied into this block
   * by the builder when the widget was placed — see
   * buildCustomWidgetInstanceBlock in the app builder, which derives the whole
   * block from the registry activation.
   *
   * **They are registry facts, not bundle claims.** Nothing the running widget
   * says over the port reaches them, which is the only reason gating on them is
   * worth anything: a bundle cannot report a version to get itself admitted.
   *
   * Both optional because blocks placed before the fields existed have neither;
   * see checkCustomWidgetCompatibility for how absence is read.
   */
  protocol?: string;
  sdkVersion?: string;
}

export function resolveGrants(instance: CustomWidgetInstanceProperties): CapabilityGrants {
  return { data: [], api: [], forms: true, nav: true, ...(instance.grants ?? {}) };
}

export interface CustomWidgetCompatibility {
  compatible: boolean;
  /** Operator/log-facing detail. Present exactly when `compatible` is false. */
  reason?: string;
}

/**
 * Whether this host will run the placed widget at all — checked *before* the frame
 * is created, so an incompatible bundle is never loaded and never gets a port.
 *
 * Two questions, both answered from the activation's manifest as the builder copied
 * it into the placed config:
 *
 * 1. `protocol` — the wire contract. The registry only ever accepts
 *    `fx-widget/1`, so a placed block claiming anything else is either from a
 *    future protocol this host predates or hand-edited config. Either way the
 *    handshake would be ignored by one side and the widget would sit blank; say so
 *    instead.
 * 2. `sdkVersion` — the vintage of fx-widget/1 the bundle was compiled against,
 *    against the platform's floor.
 *
 * Absent values are read as the grandfather case (`fx-widget/1`, SDK 1.0.0), which
 * is what every block placed before these fields existed genuinely is: the registry
 * validated `protocol` at upload, and 1.0.0 is the oldest SDK there has ever been.
 * Under today's floor those keep rendering; when the floor rises they are the first
 * to be told to republish, which is the intended consequence of raising it.
 */
export function checkCustomWidgetCompatibility(
  instance: Pick<CustomWidgetInstanceProperties, 'protocol' | 'sdkVersion'>,
  floor: string = MINIMUM_COMPATIBLE_SDK_VERSION,
): CustomWidgetCompatibility {
  const protocol = instance.protocol ?? WIDGET_PROTOCOL;
  if (protocol !== WIDGET_PROTOCOL) {
    return {
      compatible: false,
      reason: `speaks protocol ${JSON.stringify(protocol)}; this host speaks ${WIDGET_PROTOCOL}`,
    };
  }
  return isSdkVersionCompatible(manifestSdkVersion(instance), floor);
}

/**
 * Adapts the platform services to the SDK's host handler contract. Data topic
 * and api operation grants still come from the instance config; the host core
 * answers ungranted calls with capability-denied before these handlers run.
 *
 * The hand-off bridge adds the 'handoffs' data topic on top of the Ngxs topics
 * plus the handoff/state handler groups; both are scoped to `scope`, the
 * mounted instance (see custom-widget-handoff.ts).
 */
export function buildCustomWidgetHostHandlers(deps: {
  store: Store;
  accountService: AccountService;
  cardManagementService: CardManagementService;
  transactionService: TransactionService;
  savingsAccountService: SavingsAccountService;
  exchangeService: ExchangeService;
  /**
   * Navigation and hand-offs move the *page the host runs in*, so the app
   * builder canvas deliberately passes none of them and serves those handler
   * groups itself (see custom-widget-preview.ts). Absent, the `nav` group is not
   * built at all and the SDK answers a nav call with a clean host-error, exactly
   * as it does for any other unserved group.
   */
  navigationService?: NavigationService;
  router?: Router;
  location?: Location;
  workflowFormsService: WorkflowFormsService;
  alertHandlerService: AlertHandlerService;
  configService: ConfigService;
  /** Persists hand-off records across the off-origin leg; needs `router` too. */
  handoffService?: WidgetHandoffService;
  /** Identifies the mounted instance; scopes hand-off records and sdk.state. */
  scope: WidgetInstanceScope;
  /** Commits a hand-off redirect off-origin; defaults to a location assign. */
  navigateToVendor?: (url: string) => void;
  confirm?: (message: string) => boolean;
  /** SCA runner for confirmation-gated api operations; defaults to the stub. */
  confirmationRunner?: ConfirmationRunner;
  /** Performs host-mediated document downloads; documents.download needs it. */
  downloadDocumentService?: DownloadDocumentService;
  /** The customer's stored documents; documents.list/upload/delete and the stored download kinds. */
  documentService?: DocumentService;
  /** The browser's own Novu feed session; notifications.list needs it. */
  notificationService?: NotificationService;
  /** org-metadata's signed-in password change; profile.changePassword needs it. */
  passwordService?: PasswordService;
  /**
   * org-metadata-service client. The onboarding operations and the
   * onboardingStatus topic need it; without it neither is served (the operations
   * answer 'unknown-operation' and the topic 'unknown-topic', which is what an
   * ungranted widget sees anyway).
   */
  metadataService?: MetadataService;
  /** Project-selected company-registry client used by companies.search/get. */
  companiesHouseService?: CompaniesHouseService;
  /** `lookups.list` only — the platform's reference lists. */
  dataSourceService?: DataSourceService;
  /** Runs the vendor identity session in a host overlay; verifyIdentity needs it. */
  identityVerificationService?: IdentityVerificationService;
  /**
   * Performs the platform's real login redirect and logout. The auth operations
   * and the onboarding submit's `onSuccess: 'signIn' | 'goToLogin'` need it;
   * without it auth.login/auth.logout fail fast and a submit follow-up degrades
   * to 'stay' (and says so in its result). auth.status needs nothing but the
   * store, so it answers either way.
   */
  keycloakWrapperService?: KeycloakWrapperService;
  /** Disconnected before a logout, exactly as the native logout widget does. */
  socketService?: SocketService;
  /**
   * Invokes the project's backend functions. Platform-only, like the services
   * above it: an invoke runs real server-side code as the signed-in user, which
   * is not something the app builder's canvas may do on a builder's session.
   * Absent, `sdk.functions.invoke` answers "not available on this host" — the
   * same clean host-error as any other unserved handler group.
   */
  customFunctionsService?: CustomFunctionsService;
  /**
   * Drives `auth.flow.*` — the managed login flow a widget signs a customer in
   * with, rather than sending them to the project's own sign-in page. Absent
   * (the builder canvas, which resolves no real Keycloak client), the four
   * operations answer "not available on this host".
   */
  headlessAuthFlow?: HeadlessAuthFlowService;
  passwordPolicy?: () => Promise<{ policy: string }>;
  onResize: (height: number) => void;
  onEvent?: (name: string, payload: unknown) => void;
}): WidgetHostHandlers {
  const { workflowFormsService, navigationService, router, location, customFunctionsService } = deps;
  const handoffBridge =
    router && deps.handoffService
      ? buildCustomWidgetHandoffBridge({
          scope: deps.scope,
          store: deps.store,
          savingsAccountService: deps.savingsAccountService,
          configService: deps.configService,
          router,
          handoffService: deps.handoffService,
          navigateToVendor: deps.navigateToVendor,
        })
      : undefined;
  /**
   * Onboarding is one bridge serving both an operation group and a data topic,
   * because they report the same state — a verification confirmed through
   * verify.confirm has to change what onboardingStatus says, and a submit has to
   * change what flow() says. Built once per mounted instance so those cannot
   * drift apart.
   */
  /**
   * The session bridge. Built before the onboarding one and handed to it, so a
   * submit's `onSuccess: 'signIn'` runs the *same* sign-in as `sdk.auth`, lands
   * on the same page, and cannot drift from it.
   */
  const authBridge = buildCustomWidgetAuthBridge({
    store: deps.store,
    keycloakWrapperService: deps.keycloakWrapperService,
    socketService: deps.socketService,
    navigationService: deps.navigationService,
    headlessAuthFlow: deps.headlessAuthFlow,
    passwordPolicy: deps.passwordPolicy,
  });
  const onboardingBridge = deps.metadataService
    ? buildCustomWidgetOnboardingBridge({
        store: deps.store,
        metadataService: deps.metadataService,
        accountService: deps.accountService,
        identityVerificationService: deps.identityVerificationService,
        auth: authBridge,
      })
    : undefined;
  const lookupBridge =
    deps.metadataService && deps.companiesHouseService
      ? buildCustomWidgetLookupOperations(deps.companiesHouseService, deps.metadataService, deps.dataSourceService)
      : undefined;
  const topics = buildCustomWidgetDataTopics(deps.store, deps.socketService);
  const dataTopics = handoffBridge ? withHandoffsTopic(topics, handoffBridge.topic) : topics;
  return {
    data: onboardingBridge ? withOnboardingStatusTopic(dataTopics, onboardingBridge.topic) : dataTopics,
    handoff: handoffBridge?.handoff,
    state: handoffBridge?.state,
    storage: {
      get: (contract) =>
        firstValueFrom(deps.accountService.getWidgetStorage(deps.scope.widgetId, contract)).catch(
          mapStorageError,
        ),
      set: (contract, value, options) =>
        firstValueFrom(
          deps.accountService.setWidgetStorage(deps.scope.widgetId, contract, value, options?.ifRevision),
        ).catch(mapStorageError),
      remove: (contract, options) =>
        firstValueFrom(
          deps.accountService.removeWidgetStorage(deps.scope.widgetId, contract, options?.ifRevision),
        ).catch(mapStorageError),
      catalog: () =>
        firstValueFrom(deps.accountService.getWidgetStorageCatalog(deps.scope.widgetId)).catch(
          mapStorageError,
        ),
    },
    functions: customFunctionsService
      ? {
          invoke: (slug, payload) =>
            firstValueFrom(customFunctionsService.invoke(slug, payload)).catch((error) =>
              mapFunctionInvokeError(slug, error),
            ),
        }
      : undefined,
    api: buildCustomWidgetApiOperations(
      deps.store,
      deps.accountService,
      deps.cardManagementService,
      deps.transactionService,
      deps.savingsAccountService,
      deps.exchangeService,
      deps.confirmationRunner,
      deps.downloadDocumentService,
      onboardingBridge,
      authBridge,
      lookupBridge,
      deps.metadataService,
      deps.documentService,
      deps.notificationService,
      deps.passwordService,
    ),
    forms: {
      get: async (path: string) => {
        const form = workflowFormsService.getFormPart(path);
        if (form) return form.getRawValue();
        const split = path.lastIndexOf('.');
        if (split < 0) return undefined;
        return workflowFormsService.getFormPart(path.slice(0, split))?.get(path.slice(split + 1))?.value;
      },
      set: async (path: string, value: unknown) => {
        if (value !== null && typeof value === 'object') {
          workflowFormsService.patchFormValues(path, value);
          return;
        }
        const split = path.lastIndexOf('.');
        if (split < 0) return;
        workflowFormsService.patchFormValues(path.slice(0, split), { [path.slice(split + 1)]: value });
      },
      validity: async () => workflowFormsService.currentPageFormsValid(),
    },
    nav:
      navigationService && router && location
        ? {
            goToPage: async (pageId: string) => {
              const { pageUrl } = navigationService.buildPageNavigationUrl({
                pageId,
              } as NavigationOptionsType);
              await router.navigateByUrl(pageUrl);
            },
            back: async () => location.back(),
          }
        : undefined,
    ui: {
      notify: async (notification) =>
        deps.alertHandlerService.showAlertFn(notification.type, notification.message),
      // ponytail: native confirm; swap for a themed host-owned modal when design lands
      confirm: async (confirmation) =>
        (deps.confirm ?? ((message: string) => window.confirm(message)))(
          `${confirmation.title}\n${confirmation.message}`,
        ),
    },
    onResize: deps.onResize,
    onEvent: deps.onEvent,
  };
}

/**
 * Turns the functions host's invoke envelope
 * (`{ error, status, message, executionId }` — see `InvokeService.mapResult`)
 * into the protocol's error codes.
 *
 * Three rules, and the reason for each:
 *
 * - Only the caller-fault outcomes get a specific code. `not-activated` is
 *   'unknown-operation' (the slug is granted but no function answers it — the
 *   same statement `api.call` makes about an operation no host serves),
 *   `input-schema-validation` is 'schema-validation' (the platform ran the
 *   function's declared input schema and nothing else happened — exactly what
 *   storage's schema-validation means), and the function's own 400 plus an
 *   oversized body are 'invalid-payload' (the request was refused for what it
 *   contained).
 * - Every 403 collapses to 'capability-denied'. The envelope distinguishes a
 *   grant, a role and the function's own refusal; a widget can do nothing
 *   different about any of them, and the message still says which it was.
 * - Everything else is 'host-error' with the envelope's message. A timeout, a
 *   resource limit, an unconfigured secret and a crashed worker are the
 *   platform's problem, not the widget's, and inventing a finer code would let a
 *   widget branch on a distinction it cannot act on. The message is the host's
 *   own sanitised prose — author messages are scrubbed server-side — so it is
 *   safe to pass through; nothing else from the response is.
 *
 * The one exception to the third rule is 409 `capability-conflict`, and it earns
 * the exception by being the one refusal a widget CAN act on differently: it means
 * the operation was already applied once under this idempotency key and the
 * original stands. 'host-error' would tell a widget that showed a transfer button
 * exactly the opposite — "it failed" — and the honest recovery from that is a
 * retry that must not happen. 'conflict' already carries that meaning for
 * `storage.set`, so no new protocol code is invented; unlike storage's, this one
 * carries no revision details, because there is no revision involved.
 */
function mapFunctionInvokeError(slug: string, error: unknown): never {
  const response = error as {
    status?: number;
    error?: { error?: string; message?: string; details?: unknown };
    headers?: { get(name: string): string | null };
  };
  const envelope = response?.error;
  const code = envelope?.error;
  const message = envelope?.message ?? `${slug} could not be invoked`;
  if (code === 'not-activated') throw new HostError('unknown-operation', message);
  if (code === 'input-schema-validation') {
    const errors = envelope?.details;
    throw new HostError('schema-validation', message, Array.isArray(errors) ? { errors } : undefined);
  }
  if (code === 'function-bad-request' || code === 'payload-too-large') {
    throw new HostError('invalid-payload', message);
  }
  if (response?.status === 403) throw new HostError('capability-denied', message);
  if (response?.status === 409) throw new HostError('conflict', message);
  if (response?.status === 429) {
    // Retry-After is the only place the wait is a number; the message only says it in prose.
    const retryAfter = Number(response.headers?.get('Retry-After') ?? '');
    throw new HostError('rate-limited', message, {
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1,
    });
  }
  throw new HostError('host-error', message);
}

function mapStorageError(error: unknown): never {
  const response = error as {
    error?: {
      error?: string;
      message?: string;
      details?: Record<string, unknown>;
      expectedRevision?: number;
      actualRevision?: number;
      retryAfterSeconds?: number;
    };
    message?: string;
  };
  const code = response?.error?.error;
  if (code === 'schema-validation' || code === 'capability-denied') {
    throw new HostError(code, response.error?.message ?? code, response.error?.details);
  }
  if (code === 'conflict') {
    throw new HostError(code, response.error?.message ?? code, {
      expectedRevision: response.error?.expectedRevision,
      actualRevision: response.error?.actualRevision,
    });
  }
  if (code === 'rate-limited') {
    throw new HostError(code, response.error?.message ?? code, {
      retryAfterSeconds: response.error?.retryAfterSeconds ?? 60,
    });
  }
  throw new HostError('host-error', response?.message ?? 'Persistent storage request failed');
}
